import React, { useEffect, useRef, useState } from 'react'
import ReactCountryFlag from 'react-country-flag'
import { isEmpty, size } from 'lodash'
import { Search as SearchIcon } from 'react-feather'
import parsePhoneNumberFromString, {
  formatIncompletePhoneNumber,
  getCountries,
  getCountryCallingCode,
} from 'libphonenumber-js'
import {
  Box,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core'

import helpers from 'helpers'

import useStyles from './styles'
import constants from 'constants/index'

// Mesmo utilizando este componente, recomenda-se fortemente realizar as validações do número de telefone
// também no schema de validação do formulário, utilizando a própria `libphonenumber-js` para garantir
// que o número seja válido e possível para o país selecionado.

const BasePhoneInput = ({ value, onChange, ...rest }) => {
  const { COUNTRY_NAMES, MAX_PHONE_LENGTH, PAGE_SIZE } = constants.countries
  const { withPrefix } = helpers.formatters.phone
  const { onlyNumbers } = helpers.formatters

  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedCountryCode, setSelectedCountryCode] = useState('BR')
  const [searchInput, setSearchInput] = useState('')
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  const textFieldRef = useRef(null)

  const classes = useStyles()

  // Obtém a lista completa de códigos de países suportados pela lib. (ex: ['US', 'BR', 'FR'])
  const countries = getCountries()

  const initialDDI = countries.find((c) =>
    value?.startsWith(getCountryCallingCode(c)),
  )

  const phoneDetails = parsePhoneNumberFromString(withPrefix(value))
  const currentDDI = phoneDetails?.country || initialDDI || selectedCountryCode

  // Filtra os países de acordo com o valor do input de busca.
  // O filtro é aplicado sobre o nome do país, código do país (sigla) e DDI.
  // A busca por DDI considera o valor com e sem o prefixo '+'.
  const filteredCountries =
    countries.filter((country) => {
      const name = COUNTRY_NAMES[country]?.toLowerCase() || ''
      const code = country.toLowerCase()
      const ddi = getCountryCallingCode(country)
      const search = searchInput.toLowerCase()
      return (
        name.includes(search) ||
        code.includes(search) ||
        ddi.includes(search) ||
        withPrefix(ddi).includes(search)
      )
    }) || []

  const handleOpen = () => setAnchorEl(textFieldRef.current)
  const handleClose = () => {
    setAnchorEl(null)
    setVisibleCount(PAGE_SIZE)
    setSearchInput('')
  }

  const handleSelect = (country) => {
    setSelectedCountryCode(country)
    handleClose()

    const newCountryCallingCode = withPrefix(getCountryCallingCode(country))
    let restValue = value

    if (currentDDI) {
      const currentCallingCode = withPrefix(getCountryCallingCode(currentDDI))
      restValue = value?.slice(size(currentCallingCode))
    }

    onChange(newCountryCallingCode + restValue)
  }

  const handleChange = (event) => {
    const value = onlyNumbers(event.target.value || '').slice(
      0,
      MAX_PHONE_LENGTH,
    )

    onChange(withPrefix(value))
  }

  const handleSearchInputChange = (event) => {
    setSearchInput(event.target.value)
    setVisibleCount(PAGE_SIZE)
  }

  const handleScroll = (e) => {
    const bottom =
      e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight
    if (bottom && visibleCount < size(filteredCountries))
      setVisibleCount((prev) => prev + PAGE_SIZE)
  }

  useEffect(() => {
    if (!!currentDDI && currentDDI !== selectedCountryCode)
      setSelectedCountryCode(currentDDI)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  return (
    <Box>
      <TextField
        ref={textFieldRef}
        type="tel"
        value={formatIncompletePhoneNumber(value, selectedCountryCode)}
        onChange={handleChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" onClick={handleOpen}>
              <ReactCountryFlag
                countryCode={selectedCountryCode}
                svg
                className={classes.flag}
              />
            </InputAdornment>
          ),
        }}
        {...rest}
      />
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ className: classes.dropdownMenu, onScroll: handleScroll }}
      >
        <Box px={2} py={1}>
          <TextField
            placeholder="Pesquise o país"
            value={searchInput}
            onChange={handleSearchInputChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={16} />
                </InputAdornment>
              ),
            }}
            fullWidth
            size="small"
          />
        </Box>
        <Box>
          {filteredCountries.slice(0, visibleCount).map((country) => {
            const countryCallingCode = withPrefix(
              getCountryCallingCode(country),
            )
            return (
              <MenuItem key={country} onClick={() => handleSelect(country)}>
                <ListItemIcon>
                  <ReactCountryFlag
                    countryCode={country}
                    svg
                    className={classes.flag}
                  />
                </ListItemIcon>
                <Box className={classes.listItemText}>
                  <Typography>{COUNTRY_NAMES[country] || country}</Typography>
                  <Typography
                    variant="caption"
                    className={classes.countryCallingCode}
                  >
                    {countryCallingCode}
                  </Typography>
                </Box>
              </MenuItem>
            )
          })}
          {isEmpty(filteredCountries) && (
            <Box px={2} py={1}>
              <Typography variant="body2">Nenhum país encontrado</Typography>
            </Box>
          )}
        </Box>
      </Menu>
    </Box>
  )
}

export default BasePhoneInput
